import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CITE_KINDS, firstQuotePage, humanizeKey, pagesLabel, printedQuote, quoteForPage } from './citationModel';
import { useCitations } from './CitationContext';

const POPOVER_WIDTH = 288;
const MARGIN = 8;

function KindIcon({ icon, className = 'h-3 w-3' }) {
  if (icon === 'check') {
    return (
      <svg className={className} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <path d="M3 8.5l3 3 7-7" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  if (icon === 'form') {
    return (
      <svg className={className} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
        <rect x="2.5" y="2.5" width="11" height="11" rx="2" />
        <path d="M5 8l2 2 4-4" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  if (icon === 'warn') {
    return (
      <svg className={className} viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
        <path d="M8 1.5l7 12.5H1L8 1.5zm-.75 4.5v4h1.5V6h-1.5zm0 5.25v1.5h1.5v-1.5h-1.5z" />
      </svg>
    );
  }
  return (
    <svg className={className} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <path d="M4 1.75h5.5L13 5.25v9H4z" strokeLinejoin="round" />
      <path d="M9.5 1.75v3.5H13" strokeLinejoin="round" />
    </svg>
  );
}

/** Quote with the extracted value marked, so the reviewer sees what matched. */
function QuoteWithValue({ quote, value }) {
  const q = String(quote || '');
  const v = String(value ?? '').trim();
  if (!v || v.length < 2) return <>{q}</>;
  const idx = q.toLowerCase().indexOf(v.toLowerCase());
  if (idx < 0) return <>{q}</>;
  return (
    <>
      {q.slice(0, idx)}
      <mark className="rounded bg-yellow-200/80 px-0.5 text-gray-900 dark:bg-yellow-500/40 dark:text-white">
        {q.slice(idx, idx + v.length)}
      </mark>
      {q.slice(idx + v.length)}
    </>
  );
}

/**
 * Inline source chip for one value: "✓ p.51", "▢ p.114", "p.103 +1" (from other fields), "⚠ no source".
 * Click opens a popover (quote, file, page links); "View source" opens the side panel.
 */
export default function CitationChip({
  cite,
  label = '',
  value = null,
  sequence = null,
  index = 0,
  size = 'sm',
}) {
  const { openSource, defaultFileId, defaultFileName } = useCitations();
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const ref = useRef(null);
  const popRef = useRef(null);

  // Rendered in a portal: cards around the chip clip overflow, so an absolute popover was cut off.
  useLayoutEffect(() => {
    if (!open) return undefined;
    const place = () => {
      const anchor = ref.current?.getBoundingClientRect();
      if (!anchor) return;
      const height = popRef.current?.offsetHeight || 0;
      const below = window.innerHeight - anchor.bottom - MARGIN;
      const top = height && below < height + 6 && anchor.top - MARGIN > below
        ? Math.max(MARGIN, anchor.top - height - 6)
        : anchor.bottom + 6;
      const left = Math.min(
        Math.max(MARGIN, anchor.right - POPOVER_WIDTH),
        window.innerWidth - POPOVER_WIDTH - MARGIN,
      );
      setPos({ top, left });
    };
    place();
    const raf = requestAnimationFrame(place);
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (ref.current?.contains(e.target) || popRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!cite) return null;
  const meta = CITE_KINDS[cite.kind] || CITE_KINDS.page;
  const fileId = cite.fileId ?? defaultFileId;
  const fileName = cite.fileName ?? defaultFileName;
  // Never open a guessed file: without its own file (or the section's only file) the page
  // number could belong to any of the student's documents.
  const canOpen = Boolean(openSource && cite.pages?.length && fileId);
  const fileUnknown = Boolean(cite.pages?.length && !fileId && cite.kind !== 'derived');
  const text = cite.kind === 'missing' ? 'no source' : pagesLabel(cite.pages) || meta.label;
  const pad = size === 'xs' ? 'px-1 py-0 text-[10px]' : 'px-1.5 py-0.5 text-[10.5px]';

  const firstPage = firstQuotePage(cite) || cite.pages?.[0];
  const go = (page, quote = quoteForPage(cite, page)) => {
    setOpen(false);
    openSource?.({
      fileId, fileName, page, quote, quotePage: quote ? page : firstQuotePage(cite), label, value, kind: cite.kind,
      sequence, index,
    });
  };

  return (
    <span ref={ref} className="relative inline-flex align-middle">
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen((v) => !v); }}
        className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full border font-medium transition-colors ${pad} ${meta.chip}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        title={`${meta.label}${cite.pages?.length ? ` · ${pagesLabel(cite.pages)}` : ''}`}
      >
        <KindIcon icon={meta.icon} />
        <span className="tabular-nums">{text}</span>
      </button>
      {open ? createPortal(
        <div
          ref={popRef}
          role="dialog"
          style={{
            position: 'fixed', top: pos?.top ?? 0, left: pos?.left ?? 0, width: POPOVER_WIDTH, zIndex: 9999,
            visibility: pos ? 'visible' : 'hidden',
          }}
          className="max-h-[70vh] overflow-auto rounded-xl border border-gray-200 bg-white p-3 text-left shadow-xl dark:border-slate-600 dark:bg-slate-800"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 dark:text-slate-500">
                {meta.label}
              </p>
              {label ? (
                <p className="truncate text-xs font-semibold text-gray-900 dark:text-white">{label}</p>
              ) : null}
            </div>
            <KindIcon icon={meta.icon} className="h-4 w-4 shrink-0 text-gray-400" />
          </div>
          <p className="mt-1 text-[11px] text-gray-500 dark:text-slate-400">{meta.hint}</p>

          {cite.quote ? (
            <blockquote className="mt-2 max-h-28 overflow-auto rounded-lg border-l-2 border-indigo-300 bg-gray-50 px-2 py-1.5 text-[11.5px] leading-snug text-gray-700 dark:border-indigo-500 dark:bg-slate-900 dark:text-slate-200">
              “<QuoteWithValue quote={cite.quote} value={value} />”
            </blockquote>
          ) : null}

          {cite.kind === 'derived' && cite.inputs?.length ? (
            <ul className="mt-2 space-y-1">
              {cite.inputs.map((inp) => (
                <li key={`${inp.section}.${inp.field}`} className="flex items-center justify-between gap-2 text-[11px]">
                  <span className="min-w-0 text-gray-600 dark:text-slate-300">
                    {humanizeKey(inp.field)}
                    {printedQuote(inp.quote) ? (
                      <span className="block truncate text-[10.5px] text-gray-400 dark:text-slate-500" title={inp.quote}>
                        “{printedQuote(inp.quote)}”
                      </span>
                    ) : null}
                  </span>
                  {inp.pages?.length ? (
                    <button
                      type="button"
                      onClick={() => go(inp.pages[0], printedQuote(inp.quote))}
                      disabled={!openSource || !fileId}
                      className="rounded-full border border-gray-200 px-1.5 py-0.5 text-[10.5px] text-indigo-600 hover:bg-indigo-50 dark:border-slate-600 dark:text-indigo-300 dark:hover:bg-slate-700"
                    >
                      {pagesLabel(inp.pages)}
                    </button>
                  ) : (
                    <span className="text-gray-400">no page</span>
                  )}
                </li>
              ))}
            </ul>
          ) : null}

          {cite.kind !== 'derived' && cite.pages?.length > 1 ? (
            <div className="mt-2 flex flex-wrap gap-1">
              {cite.pages.map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => go(p)}
                  disabled={!openSource || !fileId}
                  className="rounded-full border border-gray-200 px-1.5 py-0.5 text-[10.5px] text-gray-600 hover:bg-gray-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700"
                >
                  p.{p}
                </button>
              ))}
            </div>
          ) : null}

          <div className="mt-3 flex items-center justify-between gap-2">
            <span className="truncate text-[10.5px] text-gray-400 dark:text-slate-500">
              {fileUnknown
                ? 'Source file not located - check the student\'s documents'
                : fileName ? `${fileName}${firstPage ? ` · page ${firstPage}` : ''}` : ''}
            </span>
            {canOpen && cite.kind !== 'derived' ? (
              <button
                type="button"
                onClick={() => go(firstPage)}
                className="shrink-0 rounded-lg bg-indigo-600 px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-indigo-700"
              >
                View source
              </button>
            ) : null}
          </div>
        </div>,
        document.body,
      ) : null}
    </span>
  );
}
